import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { firstValueFrom } from 'rxjs'

import { BaseModal } from '../../../shared/modals/base/base-modal'
import { PartsFluidsItem, PurchasingOrder, PurchasingOrderLineItem } from '../../../shared/data-models'

interface VendorOption {
    vendorId: number
    vendorName: string
}

interface EmCostTypeOption {
    costType: string
    description: string
}

const categoryKeywords: Record<string, string[]> = {
    Fluid: ['fluid', 'oil', 'lube', 'fuel', 'grease'],
    Filter: ['filter'],
    Battery: ['battery'],
    Brakes: ['brake'],
    'Tire/Track': ['tire', 'track', 'undercarriage'],
    GET: ['get', 'ground engaging', 'wear'],
}

const specificKeywordGroups = [
    categoryKeywords.Fluid,
    categoryKeywords.Filter,
    categoryKeywords.Battery,
    categoryKeywords.Brakes,
    categoryKeywords['Tire/Track'],
    categoryKeywords.GET,
]

const vistaPartFallbackKeywords = ['part', 'repair', 'material']

interface PurchasingWorkOrderOption {
    source: 'Vista' | 'WebApps'
    workOrderId?: string
    workOrder: string
    description: string
    equipment: string
    items?: { item: number; description: string }[]
}

interface PurchasingEquipmentContext {
    equipment: string
    job: string
    workOrders: PurchasingWorkOrderOption[]
}

export interface PoDraftLine {
    id: string
    itemCode: string
    description: string
    uom: string
    quantity: number
    unitPrice?: number
    source: string
    equipment: string
    notes?: string
    category?: string
    subCategory?: string
    emCostType?: string
    workOrderItem?: number
}

export interface WorkOrderItemOption {
    woitem: number
    description?: string
}

export interface PoDraftGroup {
    id: string
    suggestedVendor: string
    vendor?: number
    vendorName?: string
    requiredByDate?: string
    notes?: string
    lines: PoDraftLine[]
}

const emptyGuid = '00000000-0000-0000-0000-000000000000'
const unassignedLabel = 'Unassigned'

@Component({
    templateUrl: './create-po-from-parts.component.html',
    styleUrls: ['./work-order-details.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule],
})
export class CreatePoFromPartsComponent extends BaseModal implements OnInit {
    items: PartsFluidsItem[] = []
    workOrderItems: WorkOrderItemOption[] = []
    equipment = ''
    workOrder = ''
    workOrderId?: string
    job?: string

    vendors: VendorOption[] = []
    emCostTypes: EmCostTypeOption[] = []
    groups: PoDraftGroup[] = []
    isLoading = true
    createResults: string[] = []

    get canCreate(): boolean {
        if (this.isSaving || this.isLoading) return false
        const activeGroups = this.groups.filter(g => g.lines.length > 0)
        if (activeGroups.length === 0) return false
        return activeGroups.every(g => !!g.vendor && g.lines.every(l => l.workOrderItem != null))
    }

    ngOnInit(): void {
        this.groups = this.buildGroups(this.items ?? [])
        this.loadLookups()
        this.loadWorkOrderItems()
    }

    lineTotal(line: PoDraftLine): number {
        return Math.round((line.quantity || 0) * (line.unitPrice || 0) * 100) / 100
    }

    groupTotal(group: PoDraftGroup): number {
        return group.lines.reduce((sum, line) => sum + this.lineTotal(line), 0)
    }

    onVendorChange(group: PoDraftGroup): void {
        const match = this.vendors.find(v => v.vendorId === Number(group.vendor))
        group.vendorName = match?.vendorName ?? ''
    }

    removeLine(group: PoDraftGroup, index: number): void {
        group.lines.splice(index, 1)
    }

    moveLine(fromGroup: PoDraftGroup, index: number, targetGroupId: string): void {
        const [line] = fromGroup.lines.splice(index, 1)
        if (!line) return

        if (targetGroupId === '__new__') {
            const group = this.newGroup(unassignedLabel)
            group.lines.push(line)
            this.groups.push(group)
            return
        }

        const target = this.groups.find(g => g.id === targetGroupId)
        if (target) target.lines.push(line)
        else fromGroup.lines.splice(index, 0, line)
    }

    otherGroups(group: PoDraftGroup): PoDraftGroup[] {
        return this.groups.filter(g => g.id !== group.id)
    }

    protected override onError(_errorResult: any) {
        const message = typeof _errorResult === 'string' ? _errorResult : _errorResult?.message
        this.services.notification.error(message || 'Failed to create purchase orders.')
    }

    protected override onBeforeSave(): boolean {
        if (!this.canCreate) {
            const missingItems = this.groups.some(g => g.lines.some(l => l.workOrderItem == null))
            this.services.notification.error(
                missingItems
                    ? 'Assign a work order item to every purchase order line.'
                    : 'Assign a vendor to every group that has line items.',
            )
            return false
        }
        return true
    }

    protected override onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void): void {
        void this.createOrders()
            .then(created => resolveSave(created))
            .catch(err => rejectSave(err))
    }

    private async createOrders(): Promise<PurchasingOrder[]> {
        const activeGroups = this.groups.filter(g => g.lines.length > 0)
        const created: PurchasingOrder[] = []
        const failures: string[] = []

        for (const group of activeGroups) {
            const payload = this.buildPayload(group)
            try {
                const result = await firstValueFrom(this.services.data.http.post<PurchasingOrder>('/api/PurchasingOrder', payload))
                created.push(result)
                this.createResults.push(`Created ${result.poNumber} for ${group.vendorName || 'vendor'}.`)
            } catch (err: any) {
                const detail = typeof err?.error === 'string' ? err.error : err?.error?.title ?? err?.error?.message
                const message = `Failed to create PO for ${group.vendorName || group.suggestedVendor}: ${detail || 'unknown error'}`
                failures.push(message)
                this.createResults.push(message)
            }
        }

        if (created.length > 0 && failures.length === 0) {
            this.services.notification.success(
                created.length === 1 ? `Created purchase order ${created[0].poNumber}.` : `Created ${created.length} draft purchase orders.`,
            )
        } else if (created.length > 0) {
            this.services.notification.warning(`Created ${created.length} purchase order(s); ${failures.length} failed.`)
        }

        if (created.length === 0) {
            throw new Error(failures[0] || 'Failed to create purchase orders.')
        }

        return created
    }

    private buildPayload(group: PoDraftGroup): PurchasingOrder {
        const lineItems = group.lines.map((line, index) => {
            const lineTotal = this.lineTotal(line)
            return {
                id: emptyGuid,
                lineNumber: index + 1,
                itemCode: line.itemCode,
                description: line.description,
                uom: line.uom,
                quantity: line.quantity,
                unitPrice: line.unitPrice,
                lineTotal,
                equipment: line.equipment || this.equipment,
                workOrder: this.workOrder,
                workOrderItem: line.workOrderItem,
                emCostType: line.emCostType,
                notes: line.notes,
            } as PurchasingOrderLineItem
        })

        const subtotal = Math.round(lineItems.reduce((sum, line) => sum + (line.lineTotal ?? 0), 0) * 100) / 100
        const distinctWoItems = [...new Set(lineItems.map(line => line.workOrderItem).filter((item): item is number => item != null))]

        return {
            id: emptyGuid,
            status: 'Draft',
            orderType: 'Equipment',
            source: 'WorkOrderParts',
            equipment: this.equipment,
            workOrder: this.workOrder,
            workOrderId: this.workOrderId,
            workOrderItem: distinctWoItems.length === 1 ? distinctWoItems[0] : undefined,
            job: this.job,
            vendor: group.vendor,
            vendorName: group.vendorName,
            description: `Parts for work order ${this.workOrder}`,
            notes: group.notes,
            requiredByDate: group.requiredByDate ? new Date(group.requiredByDate) : undefined,
            orderDate: new Date(),
            subtotal,
            total: subtotal,
            lineItems,
            documents: [],
        } as unknown as PurchasingOrder
    }

    private loadLookups(): void {
        this.services.data.http.get<VendorOption[]>('/api/PurchasingOrder/GetVendors').subscribe({
            next: result => {
                this.vendors = result || []
                this.isLoading = false
            },
            error: () => {
                this.vendors = []
                this.isLoading = false
            },
        })

        this.services.data.http.get<EmCostTypeOption[]>('/api/PurchasingOrder/GetEmCostTypes').subscribe({
            next: result => {
                this.emCostTypes = result || []
                this.applySuggestedCostTypes()
            },
            error: () => {
                this.emCostTypes = []
            },
        })

        if (!this.equipment) {
            this.isLoading = false
            return
        }

        this.services.data.http.get<PurchasingEquipmentContext>(`/api/PurchasingOrder/GetEquipmentWorkOrders?equipment=${encodeURIComponent(this.equipment)}`).subscribe({
            next: result => {
                if (!this.job) this.job = result?.job
                const match = (result?.workOrders ?? []).find(
                    wo =>
                        (!!this.workOrderId && wo.workOrderId === this.workOrderId) ||
                        (!!this.workOrder && wo.workOrder === this.workOrder),
                )
                if (!this.workOrderId && match?.workOrderId) this.workOrderId = match.workOrderId
                if (this.workOrderItems.length === 0 && match?.items?.length) {
                    this.workOrderItems = match.items.map(i => ({ woitem: i.item, description: i.description }))
                }
            },
            error: () => {},
        })
    }

    private loadWorkOrderItems(): void {
        if (this.workOrderItems.length > 0) return
        if (!this.workOrder && !this.workOrderId) return

        const params = new URLSearchParams()
        if (this.equipment) params.set('equipment', this.equipment)
        if (this.workOrder) params.set('workOrder', this.workOrder)
        if (this.workOrderId) params.set('workOrderId', this.workOrderId)

        this.services.data.http.get<WorkOrderItemOption[]>(`/api/EquipmentWorkOrder/items?${params.toString()}`).subscribe({
            next: result => {
                if (this.workOrderItems.length === 0) this.workOrderItems = result || []
            },
            error: () => {},
        })
    }

    private buildGroups(items: PartsFluidsItem[]): PoDraftGroup[] {
        const groupsByVendor = new Map<string, PoDraftGroup>()

        for (const item of items) {
            const key = item.suggestedVendor?.trim() || unassignedLabel
            let group = groupsByVendor.get(key)
            if (!group) {
                group = this.newGroup(key)
                groupsByVendor.set(key, group)
            }
            group.lines.push(this.toLine(item))
        }

        const groups = Array.from(groupsByVendor.values())
        groups.sort((a, b) => {
            if (a.suggestedVendor === unassignedLabel) return 1
            if (b.suggestedVendor === unassignedLabel) return -1
            return a.suggestedVendor.localeCompare(b.suggestedVendor)
        })
        return groups
    }

    private newGroup(suggestedVendor: string): PoDraftGroup {
        return {
            id: crypto.randomUUID ? crypto.randomUUID() : `g-${Date.now()}-${Math.random()}`,
            suggestedVendor,
            lines: [],
        }
    }

    private toLine(item: PartsFluidsItem): PoDraftLine {
        const descriptionParts = [item.itemLabel, item.itemValue, item.details].filter(x => !!x && String(x).trim())
        return {
            id: crypto.randomUUID ? crypto.randomUUID() : `l-${Date.now()}-${Math.random()}`,
            itemCode: item.itemValue,
            description: descriptionParts.join(' — '),
            uom: item.um || '',
            quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
            unitPrice: item.unitCost,
            source: item.source,
            equipment: item.equipment,
            notes: item.notes,
            category: item.category,
            subCategory: item.subCategory,
        }
    }

    private applySuggestedCostTypes(): void {
        if (this.emCostTypes.length === 0) return
        for (const group of this.groups) {
            for (const line of group.lines) {
                if (line.emCostType) continue
                line.emCostType = this.suggestEmCostType(line)
            }
        }
    }

    private suggestEmCostType(line: PoDraftLine): string | undefined {
        const keywords = this.keywordsForLine(line)
        if (keywords.length === 0) return undefined
        const match = this.emCostTypes.find(ct => this.matchesKeywords(ct.description || '', keywords))
        return match?.costType
    }

    private keywordsForLine(line: PoDraftLine): string[] {
        const category = (line.category || '').trim()
        if (category === 'Vista Part') {
            const haystack = `${line.subCategory || ''} ${line.description || ''}`
            const specific = specificKeywordGroups.find(group => this.matchesKeywords(haystack, group))
            return specific ?? vistaPartFallbackKeywords
        }
        return categoryKeywords[category] ?? []
    }

    private matchesKeywords(text: string, keywords: string[]): boolean {
        if (!text || keywords.length === 0) return false
        const lower = text.toLowerCase()
        return keywords.some(keyword => {
            if (keyword.length <= 3) {
                return new RegExp(`\\b${keyword}\\b`, 'i').test(text)
            }
            return lower.includes(keyword)
        })
    }
}
